import {
  appendEquityExtraBenchmarkColumns,
  appendTxnBenchmarkColumns,
  EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
  TXN_BENCHMARK_COLUMN_ORDER,
  withTxnBenchmarkColumns,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { appendAdjustedCostPriceColumn } from "../../../utils/reports/reportHoldingColumns";
import { REPORT_GAIN_TYPE_COLUMN_LABEL } from "../../../utils/reports/reportDisplayLabels";
import {
  appendHoldingLevelCostColumns,
  orderTransactionCostBenchmarkAndMemberColumns,
  withTxnMetadataAndBenchmarkColumns,
} from "../../../utils/reports/transactionMetadataColumns";
import {
  FieldColumn,
  getColumnsForSifReportType,
  INVESTOR_COLUMN,
  SIF_ALL_COMMON_COLUMNS,
  SIF_POINT_TO_POINT_REPORT,
  SIF_RETURNS_REPORT,
  SIF_TABLE_SEGMENT,
  SIF_TRANSACTION_COLUMNS,
  SIF_UNREALIZED_HOLDING_COLUMNS,
} from "./sifReportConfig";

export const REPORT_BUILDER_VISIBLE_COLUMN_COUNT = 7;

const UNREALIZED_CAGR_COLUMN: FieldColumn = { key: "cagr", value: "CAGR", minWidth: 96 };

const PNL_COLUMN_KEYS = new Set([
  "gainLoss",
  "gainLossPercentage",
  "gainLossPerc",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
]);

const UNREALIZED_HOLDING_TYPE_COLUMN: FieldColumn = {
  key: "type",
  value: REPORT_GAIN_TYPE_COLUMN_LABEL,
  locked: true,
  minWidth: 132,
};

const REPORT_DAYS_COLUMN: FieldColumn = {
  key: "days",
  value: "Avg. Days",
  minWidth: 88,
};

const REPORT_COST_COLUMN_KEYS = [
  "stt",
  "stampDuty",
  "otherExpenses",
  "brokerage",
] as const;

const SIF_ALL_REPORT_AFTER_ADJ_COLUMNS: FieldColumn[] = [
  { key: "xirr", value: "XIRR", minWidth: 96 },
  { key: "cagr", value: "CAGR", minWidth: 96 },
];

const SIF_TXN_REPORT_HIDDEN_COLUMN_KEYS = new Set<string>([
  ...TXN_BENCHMARK_COLUMN_ORDER,
  "realisedPnl",
  "realisedPNLPercentage",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realizedPnl",
  "realizedPNLPercentage",
  "unrealizedPNL",
  "unrealizedPNLPercentage",
]);

const SIF_TXN_REPORT_COLUMN_ORDER = [
  "transactiondate",
  "schemeName",
  "category",
  "transactionType",
  "folioNo",
  "units",
  "amount",
  "currentValue",
  "currentDate",
  "cagr",
  "days",
  "stt",
  "stampDuty",
  "otherExpenses",
  "brokerage",
] as const;

const SIF_P2P_REPORT_COLUMN_ORDER = [
  "schemeName",
  "type",
  "purchaseDate",
  "currentDate",
  "days",
  "units",
  "currentNAV",
  "investedAmount",
  "currentAmount",
  "avgPrice",
  "xirr",
  "cagr",
  "unrealisedPNL",
  "unrealisedPNLPercentage",
  "realisedPnl",
  "realisedPNLPercentage",
  "folioNo",
  ...TXN_BENCHMARK_COLUMN_ORDER,
  ...EQUITY_EXTRA_BENCHMARK_COLUMN_ORDER,
  "stt",
  "stampDuty",
  "otherExpenses",
  "brokerage",
] as const;

const SIF_P2P_HIDDEN_COLUMN_KEYS = new Set([
  "transType",
  "txnType",
  "transactionType",
  "transactiondate",
  "category",
  "unrealisedPNLCombined",
  "realisedPNLCombined",
  "memberName",
]);

const withUnrealizedCagrColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "cagr")) return cols;
  const without = cols.filter((c) => c.key !== "cagr");
  const pnlIdx = without.findIndex((c) => PNL_COLUMN_KEYS.has(c.key));
  const insertAt = pnlIdx >= 0 ? pnlIdx : without.length;
  return [
    ...without.slice(0, insertAt),
    UNREALIZED_CAGR_COLUMN,
    ...without.slice(insertAt),
  ];
};

const withUnrealizedHoldingTypeColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (!cols.length) return cols;

  let result = cols.map((c) =>
    c.key === "type" ? { ...c, value: REPORT_GAIN_TYPE_COLUMN_LABEL } : c,
  );

  if (result.some((c) => c.key === "type")) {
    return withUnrealizedCagrColumn(result);
  }

  const isinIdx = result.findIndex((c) => c.key === "isin");
  const nameIdx = result.findIndex((c) => c.key === "schemeName");
  const insertAt =
    isinIdx >= 0 ? isinIdx + 1 : nameIdx >= 0 ? nameIdx + 1 : 1;

  return withUnrealizedCagrColumn([
    ...result.slice(0, insertAt),
    UNREALIZED_HOLDING_TYPE_COLUMN,
    ...result.slice(insertAt),
  ]);
};

const withTxnSchemeNameColumn = (cols: FieldColumn[]): FieldColumn[] => {
  if (cols.some((c) => c.key === "schemeName")) return cols;
  return [
    { key: "schemeName", value: "Scheme Name", locked: true, minWidth: 220 },
    ...cols,
  ];
};

const ensureReportDaysColumn = (columns: FieldColumn[]): FieldColumn[] => {
  if (columns.some((c) => c.key === "days")) return columns;
  return [...columns, REPORT_DAYS_COLUMN];
};

const stripDaysColumn = (columns: FieldColumn[]): FieldColumn[] =>
  columns.filter((c) => c.key !== "days");

const orderDaysColumnBeforeStt = (columns: FieldColumn[]): FieldColumn[] => {
  const withoutDays = columns.filter((c) => c.key !== "days");
  const daysCol = columns.find((c) => c.key === "days") ?? REPORT_DAYS_COLUMN;
  const firstCostIdx = withoutDays.findIndex((c) =>
    REPORT_COST_COLUMN_KEYS.includes(
      c.key as (typeof REPORT_COST_COLUMN_KEYS)[number],
    ),
  );
  const insertAt = firstCostIdx >= 0 ? firstCostIdx : withoutDays.length;
  return [
    ...withoutDays.slice(0, insertAt),
    daysCol,
    ...withoutDays.slice(insertAt),
  ];
};

const stripMemberNameColumn = (cols: FieldColumn[]): FieldColumn[] =>
  orderTransactionCostBenchmarkAndMemberColumns(cols, {
    includeMemberName: false,
  }) as FieldColumn[];

const orderColumnsByKeys = (
  columns: FieldColumn[],
  orderKeys: readonly string[],
): FieldColumn[] => {
  const order = new Map(orderKeys.map((key, idx) => [key, idx]));
  return [...columns].sort(
    (a, b) => (order.get(a.key) ?? 999) - (order.get(b.key) ?? 999),
  );
};

const orderTrailingColumnsAfterAdjCost = (
  columns: FieldColumn[],
  trailingColumns: FieldColumn[],
): FieldColumn[] => {
  const trailingKeySet = new Set(trailingColumns.map((c) => c.key));
  const without = columns.filter((c) => !trailingKeySet.has(c.key));
  const trailingCols = trailingColumns.map((def) => {
    const existing = columns.find((c) => c.key === def.key);
    return existing ?? def;
  });
  const adjIdx = without.findIndex((c) => c.key === "adjustedCostPrice");
  const insertAt = adjIdx >= 0 ? adjIdx + 1 : without.length;
  return [
    ...without.slice(0, insertAt),
    ...trailingCols,
    ...without.slice(insertAt),
  ];
};

const appendSifBenchmarkAndCostColumns = (
  columns: FieldColumn[],
  options?: { placeDaysBeforeStt?: boolean },
): FieldColumn[] => {
  let cols = columns;
  if (options?.placeDaysBeforeStt) {
    cols = stripDaysColumn(cols);
  }
  cols = stripMemberNameColumn(cols);
  if (options?.placeDaysBeforeStt) {
    cols = orderDaysColumnBeforeStt(ensureReportDaysColumn(cols));
  }
  return cols;
};

const buildSifHoldingPreset = (): FieldColumn[] => {
  let cols = appendAdjustedCostPriceColumn(
    withUnrealizedCagrColumn(SIF_UNREALIZED_HOLDING_COLUMNS),
  );
  cols = appendHoldingLevelCostColumns(cols, SIF_TABLE_SEGMENT);
  cols = appendTxnBenchmarkColumns(cols);
  return appendEquityExtraBenchmarkColumns(cols);
};

const buildSifHoldingColumns = (): FieldColumn[] =>
  appendSifBenchmarkAndCostColumns(
    withUnrealizedHoldingTypeColumn(buildSifHoldingPreset()),
    { placeDaysBeforeStt: true },
  );

const buildSifRealizedColumns = (): FieldColumn[] => {
  let cols = withTxnMetadataAndBenchmarkColumns(
    getColumnsForSifReportType("Realized"),
    SIF_TABLE_SEGMENT,
    { includeBenchmarks: true },
  ) as FieldColumn[];
  cols = withUnrealizedCagrColumn(cols);
  cols = appendEquityExtraBenchmarkColumns(cols);
  return stripMemberNameColumn(cols);
};

const buildSifAllColumns = (): FieldColumn[] => {
  let cols = appendTxnBenchmarkColumns(
    appendHoldingLevelCostColumns(
      appendAdjustedCostPriceColumn(SIF_ALL_COMMON_COLUMNS),
      SIF_TABLE_SEGMENT,
    ),
  );
  cols = ensureReportDaysColumn(cols);
  const keys = new Set(cols.map((c) => c.key));
  SIF_ALL_REPORT_AFTER_ADJ_COLUMNS.forEach((col) => {
    if (!keys.has(col.key)) cols.push(col);
  });
  cols = appendEquityExtraBenchmarkColumns(cols);
  cols = orderTrailingColumnsAfterAdjCost(cols, SIF_ALL_REPORT_AFTER_ADJ_COLUMNS);
  return appendSifBenchmarkAndCostColumns(cols, { placeDaysBeforeStt: true });
};

const buildSifTransactionColumns = (): FieldColumn[] => {
  let cols = withTxnSchemeNameColumn(
    appendEquityExtraBenchmarkColumns(
      withTxnMetadataAndBenchmarkColumns(
        SIF_TRANSACTION_COLUMNS,
        SIF_TABLE_SEGMENT,
        { includeBenchmarks: true },
      ) as FieldColumn[],
    ),
  );
  cols = withUnrealizedCagrColumn(cols);
  return stripMemberNameColumn(cols);
};

const buildSifTransactionDetailsColumns = (): FieldColumn[] => {
  let cols = getColumnsForSifReportType("Transaction report").filter(
    (c) => c.key !== "memberName",
  );
  cols = cols.filter((c) => !SIF_TXN_REPORT_HIDDEN_COLUMN_KEYS.has(c.key));
  cols = stripMemberNameColumn(cols);
  return orderColumnsByKeys(cols, SIF_TXN_REPORT_COLUMN_ORDER);
};

const buildSifP2pColumns = (): FieldColumn[] => {
  let cols = withTxnBenchmarkColumns(
    getColumnsForSifReportType(SIF_POINT_TO_POINT_REPORT),
  );
  cols = appendEquityExtraBenchmarkColumns(cols);
  cols = cols.filter((c) => !SIF_P2P_HIDDEN_COLUMN_KEYS.has(c.key));
  cols = stripMemberNameColumn(cols);
  return orderColumnsByKeys(cols, SIF_P2P_REPORT_COLUMN_ORDER);
};

export const getBaseColumnsForReportType = (reportType: string): FieldColumn[] => {
  switch (reportType) {
    case "Holding":
      return buildSifHoldingColumns();
    case "Realized":
      return buildSifRealizedColumns();
    case "All":
      return buildSifAllColumns();
    case "Transaction":
      return buildSifTransactionColumns();
    case "Transaction report":
      return buildSifTransactionDetailsColumns();
    case SIF_POINT_TO_POINT_REPORT:
      return buildSifP2pColumns();
    case SIF_RETURNS_REPORT:
    case "Sector report":
    case "Cap report":
      return stripMemberNameColumn(getColumnsForSifReportType(reportType));
    default:
      return getColumnsForSifReportType(reportType);
  }
};

export const buildSelectableColumns = (reportType: string): FieldColumn[] => [
  INVESTOR_COLUMN,
  ...getBaseColumnsForReportType(reportType),
];

export const buildEffectiveColumns = (
  reportType: string,
  visibleColumnKeys: string[],
): FieldColumn[] => {
  let columns = buildSelectableColumns(reportType);
  if (visibleColumnKeys.length > 0) {
    const visible = new Set(visibleColumnKeys);
    columns = columns.filter((c) => visible.has(c.key));
  }
  return columns;
};

export const getDefaultVisibleColumnKeys = (reportType: string): string[] =>
  buildSelectableColumns(reportType).map((c) => c.key);
